// ─────────────────────────────────────────────────────────────────
// Glass-i — Production Department Add/Edit modal
// (Stage 6B.8A-3K.3+ / 3K.6 execution-order extension).
//
// Filename kept as ProductionDepartmentDrawer.jsx for import stability;
// the visual surface is a centered AntD Modal (matches the Customer
// Master popup pattern from Stage 6A.2D-Modal). Compact layout:
//   • width 720, centered, body capped at 70vh with internal scroll
//   • light slate-50 header + footer bands, thin slate-200 borders
//   • tight grid columns + 4px row gaps so the form fits without scroll
//   • sticky title + footer; Save (primary) + Close (secondary)
//
// Behaviour:
//   • Main → hides Parent + Work Mode defaults to "not_applicable"
//   • Sub  → Parent required + Work Mode required
//   • Code auto-suggested from name (operator can override)
//   • Abbreviation suggested but editable
//   • Execution Order auto-fills from the selected Execution Group's
//     canonical default, but the operator can override (e.g. Packing
//     and Ready for Dispatch both at order = 90).
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, Switch, Button, Alert
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import {
  DEPARTMENT_TYPES, WORK_MODES, EXECUTION_GROUPS,
  suggestDepartmentCode, suggestAbbreviation,
  DEFAULT_EXECUTION_FLOW
} from './productionDepartmentsHelpers';
import './ProductionDepartmentDrawer.css';

const DEFAULT_ORDER_BY_GROUP = DEFAULT_EXECUTION_FLOW.reduce((acc, s) => {
  acc[s.group] = s.order; return acc;
}, {});

export default function ProductionDepartmentDrawer({
  open, mode, initialValues, mainOptions, onClose, onSubmit, submitting
}) {
  const [form] = Form.useForm();

  const isEdit = mode === 'edit';
  const titleText = isEdit ? 'Edit Department'
    : (initialValues && initialValues.departmentType === 'sub'
        ? 'Add Sub Department' : 'Add Main Department');

  // Sync form when the modal opens with new initialValues.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        departmentType:    'main',
        workMode:          null,
        sequenceNo:        1,
        isProductionStage: true,
        isQcStage:         false,
        isActive:          true,
        // ── Stage 6B.8A-3K.6 defaults ──
        executionOrder:        null,
        executionGroup:        null,
        isRouteStage:          true,
        allowParallelExecution: false,
        ...(initialValues || {})
      });
    }
  }, [open, initialValues, form]);

  const typeValue = Form.useWatch('departmentType', form) || 'main';
  const isSub  = typeValue === 'sub';
  const isMain = typeValue === 'main';

  const onNameBlur = (e) => {
    const name = e.target.value;
    if (!name) return;
    const currentCode = form.getFieldValue('departmentCode');
    const currentAbbr = form.getFieldValue('abbreviation');
    if (!currentCode || !String(currentCode).trim()) {
      form.setFieldsValue({ departmentCode: suggestDepartmentCode(name) });
    }
    if (!currentAbbr || !String(currentAbbr).trim()) {
      form.setFieldsValue({ abbreviation: suggestAbbreviation(name) });
    }
  };

  // When the operator picks an execution group, suggest the canonical
  // execution order (only when the order field is currently blank — we
  // never overwrite a manual value).
  const onExecutionGroupChange = (group) => {
    const current = form.getFieldValue('executionOrder');
    if (current != null && current !== '') return;
    if (group && Object.prototype.hasOwnProperty.call(DEFAULT_ORDER_BY_GROUP, group)) {
      form.setFieldsValue({ executionOrder: DEFAULT_ORDER_BY_GROUP[group] });
    }
  };

  const handleOk = async () => {
    let values;
    try { values = await form.validateFields(); } catch (_e) { return; }
    if (values.departmentType === 'main') {
      values.parentDepartmentId = null;
      if (!values.workMode) values.workMode = 'not_applicable';
    }
    onSubmit && onSubmit(values);
  };

  const parentOptions = useMemo(() => {
    if (!Array.isArray(mainOptions)) return [];
    return mainOptions.map((m) => ({
      value: m.id,
      label: `${m.departmentName} (${m.departmentCode})`
    }));
  }, [mainOptions]);

  const footerNode = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button type="primary" icon={<SaveOutlined />}
        loading={submitting} onClick={handleOk}>
        {isEdit ? 'Save Changes' : 'Add Department'}
      </Button>
      <Button icon={<CloseOutlined />} onClick={onClose} disabled={submitting}>
        Close
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={titleText}
      width={760}
      centered
      destroyOnClose
      maskClosable={!submitting}
      className="erp-prod-dept-modal"
      styles={{
        body: {
          padding: '12px 16px',
          background: '#ffffff',
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      footer={footerNode}
    >
      <Form form={form} layout="vertical" requiredMark="optional"
            preserve={false} className="erp-prod-dept-form">

        <div className="pd-row pd-row-3">
          <Form.Item label="Type" name="departmentType" required>
            <Select options={DEPARTMENT_TYPES} disabled={isEdit} />
          </Form.Item>
          {isSub ? (
            <Form.Item label="Parent Department" name="parentDepartmentId"
              rules={[{ required: true,
                         message: 'Sub department must have a parent department.' }]}
              style={{ gridColumn: 'span 2' }}>
              <Select options={parentOptions} showSearch optionFilterProp="label"
                placeholder="Select the main department"
                notFoundContent="No main departments yet." />
            </Form.Item>
          ) : (
            <Form.Item label="Sequence No" name="sequenceNo"
              style={{ gridColumn: 'span 2' }}>
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
          )}
        </div>

        {isMain && (
          <Alert type="info" showIcon
            style={{ marginBottom: 10, padding: '4px 10px', fontSize: 12 }}
            message="Main departments do not have a parent. Pick ‘Sub’ to nest Manual / Machine variants under them." />
        )}

        <Form.Item label="Department Name" name="departmentName" required
          rules={[{ required: true, message: 'Department name is required.' },
                  { max: 160, message: 'Name is too long.' }]}>
          <Input autoFocus onBlur={onNameBlur}
            placeholder="e.g., Manual Cutting" />
        </Form.Item>

        <div className="pd-row pd-row-2">
          <Form.Item label="Department Code" name="departmentCode"
            rules={[{ max: 40 }]}>
            <Input placeholder="CUT_MAN (auto from name)" />
          </Form.Item>
          <Form.Item label="Abbreviation" name="abbreviation"
            rules={[{ max: 30 }]}>
            <Input placeholder="MC" />
          </Form.Item>
        </div>

        <div className="pd-row pd-row-2">
          <Form.Item label="Work Mode" name="workMode"
            rules={isSub ? [{ required: true,
                              message: 'Work mode is required for sub departments.' }] : []}>
            <Select options={WORK_MODES} allowClear
              placeholder={isMain ? 'Not Applicable' : 'Manual / Machine / Mixed / …'} />
          </Form.Item>
          {isSub && (
            <Form.Item label="Sequence No" name="sequenceNo">
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
          )}
          {isMain && (
            <Form.Item label="Remarks" name="remarks">
              <Input placeholder="Optional notes" maxLength={200} />
            </Form.Item>
          )}
        </div>

        {/* ── Stage 6B.8A-3K.6 — Execution flow ─────────────────── */}
        <div className="pd-row pd-row-2">
          <Form.Item label="Execution Group" name="executionGroup"
            tooltip="Coarse stage label shared across all departments at this step (e.g. Polish covers Manual + Machine Polishing).">
            <Select options={EXECUTION_GROUPS} allowClear
              placeholder="Pick the production stage"
              onChange={onExecutionGroupChange}
              showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Execution Order" name="executionOrder"
            tooltip="Where this stage falls in the production flow. Manual + Machine alternatives at the same stage may share the same number (e.g. Polish = 20 / 20)."
            rules={[{
              validator: (_r, v) =>
                v == null || v === '' || Number(v) >= 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(
                      'Execution order must be zero or greater.'))
            }]}>
            <InputNumber min={0} step={10} style={{ width: '100%' }}
              placeholder="10 / 20 / 30 / …" />
          </Form.Item>
        </div>

        <div className="pd-row pd-row-3 pd-switches">
          <Form.Item label="Route Stage?" name="isRouteStage"
            valuePropName="checked"
            tooltip="Whether this department can appear on the production route.">
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>
          <Form.Item label="Allow Parallel?" name="allowParallelExecution"
            valuePropName="checked"
            tooltip="Whether this stage can run in parallel with another (used by future planning).">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </div>

        <div className="pd-row pd-row-3 pd-switches">
          <Form.Item label="Production Stage" name="isProductionStage"
            valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item label="QC Stage" name="isQcStage" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </div>

        {isSub && (
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} maxLength={500}
              placeholder="Optional notes" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
